import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { FiArrowLeft } from 'react-icons/fi';
import { getInstanceHistory } from '../../services/monitorService';
import { fmtMoney, fmtProfit, fmtNum, fmtTime, profitClass } from '../../utils/format';

const RANGES = [
  { key: '1h', name: '1 小时' },
  { key: '6h', name: '6 小时' },
  { key: '24h', name: '24 小时' },
  { key: '7d', name: '7 天' }
];

const InstanceDetail = () => {
  const { instanceId } = useParams();
  const [range, setRange] = React.useState('24h');

  const { data, isLoading, error } = useQuery(
    ['instanceHistory', instanceId, range],
    () => getInstanceHistory(instanceId, range),
    { refetchInterval: 30000 }
  );

  const points = data?.points || [];
  const latest = points.length > 0 ? points[points.length - 1] : null;

  // 图表数据(横轴时间, 纵轴金额)
  const chartData = points.map(p => ({
    t: fmtTime(p.t).slice(5),
    equity: Number(p.equity) || 0,
    dayProfit: Number(p.dayProfit) || 0,
    balance: Number(p.balance) || 0
  }));

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center gap-3">
        <Link to="/monitor/instances" className="text-[#57606a] hover:text-[#1f2328]">
          <FiArrowLeft className="text-xl" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">账户明细</h1>
          <p className="text-[#656d76] text-sm mt-1 font-mono">{decodeURIComponent(instanceId || '')}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="skeleton h-24" />
          <div className="skeleton h-80" />
        </div>
      ) : error ? (
        <div className="bg-white rounded-md border border-[#d0d7de] p-8 text-center text-[#cf222e]">
          获取数据失败: {error.message || '未知错误'}
        </div>
      ) : points.length === 0 ? (
        <div className="bg-white rounded-md border border-[#d0d7de] p-16 text-center">
          <p className="text-[#656d76] text-sm">该时间范围内暂无数据</p>
        </div>
      ) : (
        <>
          {/* 当前数值卡片 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
              <p className="text-[#656d76] text-xs mb-1">净值</p>
              <p className="text-xl font-semibold text-[#1f2328]">{fmtMoney(latest.equity)}</p>
            </div>
            <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
              <p className="text-[#656d76] text-xs mb-1">余额</p>
              <p className="text-xl font-semibold text-[#1f2328]">{fmtMoney(latest.balance)}</p>
            </div>
            <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
              <p className="text-[#656d76] text-xs mb-1">浮动盈亏</p>
              <p className={`text-xl font-semibold ${profitClass(latest.floatingProfit)}`}>{fmtProfit(latest.floatingProfit)}</p>
            </div>
            <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-4">
              <p className="text-[#656d76] text-xs mb-1">当日盈亏</p>
              <p className={`text-xl font-semibold ${profitClass(latest.dayProfit)}`}>{fmtProfit(latest.dayProfit)}</p>
            </div>
          </div>

          {/* 时间范围切换 */}
          <div className="flex items-center gap-2">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  range === r.key
                    ? 'bg-[#ddf4ff] text-[#0969da] border-[#b6e3ff] font-medium'
                    : 'bg-white text-[#57606a] border-[#d0d7de] hover:bg-[#f6f8fa]'
                }`}
              >
                {r.name}
              </button>
            ))}
            <span className="text-[#8b949e] text-xs ml-2">共 {points.length} 个数据点</span>
          </div>

          {/* 净值曲线 */}
          <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-5">
            <h2 className="text-[#1f2328] font-medium mb-4">净值 / 当日盈亏曲线</h2>
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eaeef2" />
                <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#656d76' }} minTickGap={40} />
                <YAxis tick={{ fontSize: 11, fill: '#656d76' }} width={70} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '0.5px solid #d0d7de' }}
                  formatter={(value, name) => [fmtMoney(value), name === 'equity' ? '净值' : name === 'dayProfit' ? '当日盈亏' : '余额']}
                />
                <Legend formatter={(v) => v === 'equity' ? '净值' : v === 'dayProfit' ? '当日盈亏' : '余额'} />
                <Line type="monotone" dataKey="equity" stroke="#0969da" strokeWidth={2} dot={false} name="equity" />
                <Line type="monotone" dataKey="dayProfit" stroke="#cf222e" strokeWidth={1.5} dot={false} name="dayProfit" />
                <Line type="monotone" dataKey="balance" stroke="#8b949e" strokeWidth={1} strokeDasharray="4 4" dot={false} name="balance" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* 提示 */}
          <p className="text-[#8b949e] text-xs">
            提示: 平仓/暂停等操作请前往 <Link to="/commands" className="text-[#0969da] hover:underline">指令中心</Link> 执行, 本页仅展示数据。
          </p>
        </>
      )}
    </div>
  );
};

export default InstanceDetail;
